import "./BlockWebsites.scss";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
    getBlockWebsiteKeywords,
    addBlockWebsiteKeywords,
    removeBlockWebsiteKeywords,
    retryFn,
} from "utils/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef, createColumnHelper, RowData, RowModel } from "@tanstack/react-table";
import AbunTable, { IndeterminateCheckbox } from "components/AbunTable/AbunTable";
import Modal from "../../components/Modal/Modal";
import TextArea from "../../components/TextArea/TextArea";
import Alert from "../../components/Alert/Alert";
import CSVUpload from "components/CSVUpload/CSVUpload";

interface BlockedWebsites {
    keyword: string
}

let err_message = "";

type Tab = 'textarea' | 'csv';

// A simple page that shows a list of block website keywords, allows adding new ones one at a time and deleting existing ones in bulk.
export default function BlockWebsites() {
    // ---------------------- NON STATE CONSTANTS ----------------------
    const pageSizes = [15, 50, 100];

    // -------------------------- STATES --------------------------
    const [blockedWebsitesKeywords, setBlockedWebsitesKeywords] = useState<Array<BlockedWebsites>>([]);
    const [selectedRows, setSelectedRows] = useState<RowModel<RowData>>();
    const [addWebsitesToBlockModal, setAddWebsitesToBlockModal] = useState(false);
    const [RemoveActionConfirmationModal, setRemoveActionConfirmationModal] = useState(false);
    const [tab, setTab] = useState<Tab>('textarea');
    const [websitesKeywordSearch, setWebsitesKeywordSearch] = useState("");

    // -------------------------- QUERIES --------------------------
    const {
        isFetching,
        isError,
        data,
        refetch,
    } = useQuery({
        queryKey: ['getBlockWebsiteKeywords'],
        queryFn: getBlockWebsiteKeywords,
        refetchOnWindowFocus: false,
        retry: retryFn
    });

    // -------------------------- MUTATIONS --------------------------
    const addBlockWebsiteKeywordsMutation = useMutation({
        mutationKey: ['addBlockWebsiteKeywords'],
        mutationFn: addBlockWebsiteKeywords,
        gcTime: 0,
        onSuccess: (response) => {
            if (!err_message) {
                successAlert.current?.show("Keywords have been blocked successfully!");
                err_message = "";
            } else {
                errorAlert.current?.show(err_message);
                err_message = "";
            }
            setWebsitesKeywordSearch("");
            setBlockedWebsitesKeywords(response.data.all_keywords.reverse());
        },
        onError: (error) => {
            errorAlert.current?.show(`Failed to save block website keywords: ${error}`);
            setWebsitesKeywordSearch("");
        },
    });

    const removeBlockWebsitesMutation = useMutation({
        mutationKey: ['removeBlockWebsiteKeywords'],
        mutationFn: removeBlockWebsiteKeywords,
        gcTime: 0,
        onSuccess: (response) => {
            successAlert.current?.show("Keyword(s) have been removed successfully!");
            setWebsitesKeywordSearch("");
            setBlockedWebsitesKeywords(response.data.all_keywords.reverse());
        },
        onError: (error) => {
            errorAlert.current?.show(`Failed to remove block website keywords: ${error}`);
            setWebsitesKeywordSearch("");
        },
    })

    // ---------------------- EFFECTS ----------------------
    useEffect(() => {
        if (data) {
            setBlockedWebsitesKeywords(data['data'].reverse());
        }
    }, [data]);

    // --------------------- REFS ---------------------
    const errorAlert = useRef<any>(null);
    const successAlert = useRef<any>(null);

    // ---------------------- FUNCTIONS ----------------------
    function selectedRowsSetter(rowModel: RowModel<RowData>) {
        setSelectedRows(rowModel);
    }

    function addKeywordsDone() {
        setAddWebsitesToBlockModal(false);
        successAlert.current?.show("Keyword(s) have been blocked successfully!");

        setTimeout(() => {
            refetch().then(() => {
            });
        }, 3000)
    }

    function onAddWebsiteKeywordsToBlock(UserInputs: string) {
        // split the input by comma or newline
        const allKeywords = UserInputs?.split(",").join("\n").split("\n");
        let validKeywords: Array<string> = [];
        allKeywords?.forEach(input => {
            if (input.trim()) {
                validKeywords.push(input.trim());
            }
        });
        if (validKeywords.length < 1) {
            errorAlert.current?.show("Please enter at least one valid keyword. ");
            return;
        } else if (validKeywords.length > 100000) {
            errorAlert.current?.show("Max 100k keywords should be submitted at once.");
            return;
        }else {
            setAddWebsitesToBlockModal(false);
            addBlockWebsiteKeywordsMutation.mutate({
                keywords: validKeywords
            });
        }
    }

    function onremoveBlockWebsiteKeywords(websiteKeywords: Array<string>) {
        if (websiteKeywords.length < 1) {
            errorAlert.current?.show("Please select at least one keyword to remove.");
            return;
        }
        // on remove multiple selected keywords from the list
        removeBlockWebsitesMutation.mutate({
            keywords: websiteKeywords
        });
    }

    // ---------------------- TABLE COLUMN DEFS ----------------------
    const columnHelper = createColumnHelper<BlockedWebsites>();
    const columnDefs: ColumnDef<any, any>[] = [
        columnHelper.accessor((row: BlockedWebsites) => row.keyword, {
            id: 'checkbox',
            header: ({ table }) => (
                <IndeterminateCheckbox
                    {...{
                        checked: table.getIsAllRowsSelected(),
                        indeterminate: table.getIsSomeRowsSelected(),
                        onChange: table.getToggleAllRowsSelectedHandler(),
                    }}
                />
            ),
            cell: ({ row }) => (
                <IndeterminateCheckbox
                    {...{
                        checked: row.getIsSelected(),
                        disabled: !row.getCanSelect(),
                        indeterminate: row.getIsSomeSelected(),
                        onChange: row.getToggleSelectedHandler(),
                    }}
                    name={"blockWebsiteKeywordSelection"}
                    value={row.original.keyword}
                />
            ),
            enableGlobalFilter: true,
        }),
        columnHelper.accessor((row: BlockedWebsites) => row.keyword, {
            id: 'websiteKeyword',
            header: "Website Keyword",
            cell: info => info.getValue(),
            enableGlobalFilter: true,
        }),
        columnHelper.display({
            id: 'removeBlockKeyword',
            header: () => (<div style={{ textAlign: "center" }}>Remove Keyword</div>),
            cell: props => {
                return (
                    <button className={"button is-danger is-small is-outlined"}
                        onClick={() => {
                            props.row.toggleSelected();
                            setRemoveActionConfirmationModal(true);
                        }
                        }>
                        Remove&nbsp;&nbsp;
                    </button>
                );
            },
            enableGlobalFilter: false,
            meta: {
                align: 'center'
            }
        }),
    ]
    // ============================================================
    // --------------------- MAIN RENDER CODE ---------------------
    // ============================================================
    if (isFetching) {
        return (
            <p style={{ textAlign: "center", fontSize: "1.3rem" }} className="mt-5">
                Loading Data...<FontAwesomeIcon icon={"spinner"} className={"ml-5"} />
            </p>
        )
    } else if (isError) {
        return (
            <section className="section">
                <div className="container">
                    <div className="box">
                        <h1 className="title has-text-centered">Blocked Website Keywords</h1>
                        <p className="has-text-centered is-size-5">
                            Failed to load data. Please try again later.
                        </p>
                    </div>
                </div>
            </section>
        );
    } else {
        return (
            <section className="section">
                <div className="container">
                    <div className="box">
                        {/* ******************* Add Block Website Keywords Modal ******************* */}
                        <Modal active={addWebsitesToBlockModal}
                            headerText={"Add Website Keywords to Block"}
                            closeable={true}
                            hideModal={() => {
                                setAddWebsitesToBlockModal(false);
                                setWebsitesKeywordSearch("");
                            }
                            }>
                            <div className="tabs">
                                <ul>
                                    <li className={tab === 'textarea' ? "is-active" : ""} onClick={() => setTab('textarea')}>
                                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                        <a>Enter Manually</a>
                                    </li>
                                    <li className={tab === 'csv' ? "is-active" : ""} onClick={() => setTab('csv')}>
                                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                        <a>Use CSV File</a>
                                    </li>
                                </ul>
                            </div>
                            <div className="tab-content">
                                {tab === 'textarea' ?
                                    <TextArea
                                        className={"textarea"}
                                        id={"addBlockWebsiteKeywords"}
                                        name={"addBlockWebsiteKeywords"}
                                        placeholder={"Add keywords to block (separated by commas or newlines)"}
                                        value={websitesKeywordSearch}
                                        onChange={(event) => setWebsitesKeywordSearch(event.target.value)}
                                    />
                                    :
                                    <CSVUpload csvUploadDone={addKeywordsDone} title="Choose column with keyword data" name="keywords"
                                        mutation={addBlockWebsiteKeywordsMutation} />}
                            </div>
                            <button className={"button is-primary is-fullwidth mt-4"}
                                onClick={() => onAddWebsiteKeywordsToBlock(websitesKeywordSearch)}
                                style={{ display: tab === 'textarea' ? 'block' : 'none' }}>
                                Add keywords to Block
                            </button>
                        </Modal>

                        {/* ******************* Remove confirmation Modal ******************* */}
                        <Modal active={RemoveActionConfirmationModal}
                            headerText={""}
                            closeable={true}
                            hideModal={() => setRemoveActionConfirmationModal(false)}>
                            <h1 className="title has-text-centered">Are you sure you want to remove the selected keyword(s)?</h1>
                            <div className="buttons is-centered">
                                <button className={"button is-danger is-outlined"}
                                    onClick={() => {
                                        setRemoveActionConfirmationModal(false);
                                        if (selectedRows && selectedRows.rows.length > 0) {
                                            const selectedKeywords = selectedRows.rows.map(row => {
                                                return (row.original as BlockedWebsites).keyword
                                            });
                                            onremoveBlockWebsiteKeywords(selectedKeywords);
                                            selectedRows.rows.forEach(row => {
                                                row.toggleSelected();
                                            });
                                        }
                                    }}>
                                    Remove Selected
                                </button>
                                <button className={"button is-primary is-outlined"}
                                    onClick={() => setRemoveActionConfirmationModal(false)}>
                                    Cancel
                                </button>
                            </div>
                        </Modal>

                        <AbunTable tableContentName={"Blocked Website Keywords"}
                            tableData={
                                blockedWebsitesKeywords.map((keyword) => {
                                    return {
                                        keyword: keyword
                                    }
                                })
                            }
                            columnDefs={columnDefs}
                            pageSizes={pageSizes}
                            initialPageSize={pageSizes[0]}
                            noDataText={
                                blockedWebsitesKeywords.length === 0 ?
                                    "No blocked keywords available."
                                    : "No keywords found."
                            }
                            searchboxPlaceholderText={"Search keywords..."}
                            rowCheckbox={true}
                            selectedRowsSetter={selectedRowsSetter}
                            buttons={[
                                {
                                    text: "Add Website Keywords to Block",
                                    type: "primary",
                                    clickHandler: () => setAddWebsitesToBlockModal(true)
                                },
                                {
                                    text: "Remove All Selected",
                                    type: "danger",
                                    clickHandler: () => setRemoveActionConfirmationModal(true),
                                    invisible: !selectedRows || selectedRows.rows.length < 1
                                },
                                {
                                    text: "Refresh",
                                    type: "primary",
                                    clickHandler: () => refetch()
                                }
                            ]} />
                    </div>
                </div>
                <Alert type={"danger"} ref={errorAlert} />
                <Alert type={"success"} ref={successAlert} />
            </section>
        );
    }
}
